import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { IResponse } from '@interfaces/index';

// Deliberately does not extend or delegate to the shared HttpService, so the
// portal's own rule — every call rethrows on failure — cannot be changed from
// underneath it by future work on the shared path (TK-030, D1/D3).
//
// The shared HttpService.get()'s `new_apis` lookup is not reproduced here:
// every endpoint the portal calls is already on the new API, so the branch
// would have exactly one outcome. A future admin endpoint added to the legacy
// backend is therefore a deliberate decision, not a silent 404.
//
// getLegacy() is that deliberate decision, spelled out per call rather than
// resolved through a lookup table: it always builds against
// `environment.api_legacy`, never `environment.api`, so reaching the legacy
// backend from the admin portal is visible at the call site (TK-084/B1).
//
// The bearer token is not read here — reading the session cookie is confined
// to a guard, an interceptor or the auth/session service (token-access-confined).
// `AdminAuthInterceptor` attaches `Authorization` to every outgoing request;
// this service only sets the fixed `Content-Type` header.
@Injectable({
  providedIn: 'root',
})
export class AdminHttpService {
  constructor(private _http_client: HttpClient) {}

  private _createHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  private _getFullUrl(url: string): string {
    return `${environment.api}/api/${url}`;
  }

  private _getLegacyFullUrl(url: string): string {
    return `${environment.api_legacy}/api/${url}`;
  }

  get<T>(url: string, params?: HttpParams): Observable<IResponse<T>> {
    const full_url = this._getFullUrl(url);
    const headers = this._createHeaders();
    return this._http_client
      .get<IResponse<T>>(full_url, { params, headers })
      .pipe(catchError((error: unknown) => throwError(() => error)));
  }

  getLegacy<T>(url: string, params?: HttpParams): Observable<IResponse<T>> {
    const full_url = this._getLegacyFullUrl(url);
    const headers = this._createHeaders();
    return this._http_client
      .get<IResponse<T>>(full_url, { params, headers })
      .pipe(catchError((error: unknown) => throwError(() => error)));
  }

  post<T>(url: string, data: unknown): Observable<IResponse<T>> {
    const full_url = this._getFullUrl(url);
    const headers = this._createHeaders();
    return this._http_client
      .post<IResponse<T>>(full_url, data, { headers })
      .pipe(catchError((error: unknown) => throwError(() => error)));
  }

  put<T>(url: string, data: unknown): Observable<IResponse<T>> {
    const full_url = this._getFullUrl(url);
    const headers = this._createHeaders();
    return this._http_client
      .put<IResponse<T>>(full_url, data, { headers })
      .pipe(catchError((error: unknown) => throwError(() => error)));
  }

  delete<T>(url: string, params?: HttpParams): Observable<IResponse<T>> {
    const full_url = this._getFullUrl(url);
    const headers = this._createHeaders();
    return this._http_client
      .delete<IResponse<T>>(full_url, { params, headers })
      .pipe(catchError((error: unknown) => throwError(() => error)));
  }

  // Named deliberately, rather than a legacy flag on post(): reaching the legacy
  // backend must stay visible and grep-able at the call site (TK-086/B1). The
  // response is a raw Blob, not the IResponse<T> envelope every other method here
  // returns, so it cannot share post()'s return type.
  downloadLegacy(url: string, data: unknown): Observable<Blob> {
    const full_url = this._getLegacyFullUrl(url);
    const headers = this._createHeaders();
    return this._http_client
      .post(full_url, data, { headers, responseType: 'blob' })
      .pipe(catchError((error: unknown) => throwError(() => error)));
  }
}
